import React from 'react';
import { Box, styled, Typography } from '@mui/material';
import { PrintablePage, Label, Value } from './styles';
import { ConditionDetails } from '../../types/genomic';


const LimitationBox = styled(Box)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.warning.light}`,
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

export const OutlinkButton = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));

const IdBadge = styled('span')(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(1),
  padding: theme.spacing(0),
  paddingLeft: theme.spacing(1),
  borderLeft: `1px solid ${theme.palette.divider}`,
}));

interface Props {
  condition: ConditionDetails;
}

const getInheritanceDescription = (inheritance: string): string => {
  switch (inheritance.toLowerCase()) {
    case 'autosomal dominant':
      return 'Only one copy of the altered gene is needed to cause the condition';
    case 'autosomal recessive':
      return 'Two copies of the altered gene are needed to cause the condition';
    case 'x-linked':
      return 'The altered gene is located on the X chromosome';
    case 'multifactorial':
      return 'Multiple genetic and environmental factors contribute to the condition';
    default:
      return '';
  }
};

export const ConditionDetailsSection: React.FC<Props> = ({ condition }) => {

  const defaultManagement = `Management of ${condition.name} requires a comprehensive approach and should be tailored 
  to individual patient needs. Consultation with appropriate specialists is recommended 
  for developing personalized management plans.`

  return (
    <PrintablePage>
      <Typography variant="h2" gutterBottom color="primary">
        {condition.name}
      </Typography>
      
      {/* Clinical Information */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom>Clinical Information</Typography>
        <Typography paragraph>{condition.description}</Typography>
        
        {condition.inheritance_pattern && condition.inheritance_pattern.length > 0 && (
          <Box mb={2}>
            <Label>Inheritance Pattern</Label>
            <Value paragraph>
              {condition.inheritance_pattern} - {getInheritanceDescription(condition.inheritance_pattern)}
            </Value>
          </Box>
        )}
      </Box>

      {/* Genetic Information
      <Box mb={4}>
        <Typography variant="h6" gutterBottom>Genetic Basis</Typography>
      </Box> */}

      {/* External Resources */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom>External Resources</Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          {condition.omim_id && (
            <OutlinkButton 
              href={`https://omim.org/entry/${condition.omim_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              OMIM Database
              <IdBadge>{condition.omim_id}</IdBadge>
            </OutlinkButton>
          )}
          {condition.orphanet_id && (
            <OutlinkButton 
              href={`https://www.orpha.net/consor/cgi-bin/OC_Exp.php?lng=en&Expert=${condition.orphanet_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              GARD - Genetic and Rare Diseases
              <IdBadge>{condition.orphanet_id}</IdBadge>
            </OutlinkButton>
          )}
          {condition.medgen_id && (
            <OutlinkButton 
              href={`https://www.ncbi.nlm.nih.gov/medgen/${condition.medgen_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              MedGen
              <IdBadge>{condition.medgen_id}</IdBadge>
            </OutlinkButton>
          )}
          {condition.mondo_id && (
            <OutlinkButton 
              href={`https://www.ebi.ac.uk/spot/oxo/terms/${condition.mondo_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Mondo
              <IdBadge>{condition.mondo_id}</IdBadge>
            </OutlinkButton>
          )}
        </Box>
      </Box>

      {/* Testing Limitations */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom>Testing Limitations</Typography>
        <LimitationBox>
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <Typography component="li" variant="body2" paragraph>
              This genetic test analyzes specific regions of genes associated with {condition.name}. 
              Not all possible disease-causing variants may be detected.
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              Some types of variants, such as certain structural or complex rearrangements, 
              may not be detectable by the methods used.
            </Typography>
            {condition.limitations && condition.limitations.map((limitation, index) => (
              <Typography component="li" variant="body2" paragraph key={index}>
                {limitation}
              </Typography>
            ))}
            {(condition.inheritance_pattern || '').length == 0 && (
              <Typography component="li" variant="body2" paragraph>
                This condition may be due to a complex genetic or environmental cause or a combination of factors which this test does not address.
              </Typography>
            )}
            {condition.inheritance_pattern === 'Autosomal Dominant' && (
              <Typography component="li" variant="body2" paragraph>
                For autosomal dominant conditions like {condition.name}, a negative result 
                does not completely rule out the condition due to possible variations in 
                penetrance and expressivity.
              </Typography>
            )}
            {condition.inheritance_pattern === 'Autosomal Recessive' && (
              <Typography component="li" variant="body2" paragraph>
                For autosomal recessive conditions, this test may not detect all possible 
                disease-causing variants. A negative result does not rule out carrier status.
              </Typography>
            )}
          </Box>
        </LimitationBox>
      </Box>

      {/* Clinical Management */}
      <Box>
        <Typography variant="h6" gutterBottom>Clinical Management Considerations</Typography>
        <Typography variant="body2" paragraph>
          {(condition.clinical_management_considerations)?condition.clinical_management_considerations:defaultManagement}
        </Typography>
        {condition.name.includes('Cancer') && (
          <Typography variant="body2" paragraph>
            Regular surveillance and risk-reduction strategies should be discussed with healthcare 
            providers. Family members may also benefit from genetic counseling and testing.
          </Typography>
        )}
        {condition.name.includes('Drug Response') && (
          <Typography variant="body2" paragraph>
            Medication adjustments should only be made under the guidance of healthcare providers 
            who can consider all relevant clinical factors.
          </Typography>
        )}
      </Box>
    </PrintablePage>
  );
}; 